import type { Session } from "../types";

export interface ScenarioInProgress {
  id: string;
  title: string;
  sessionCount: number;
}

/** Just enough of a scenario for the crumb; matches getScenarioLibrary's rows. */
export interface ScenarioTitle {
  id: string;
  title: string;
}

/**
 * The scenarios this user has actually started, most recently touched first.
 *
 * Driven by sessions rather than by the library, because the crumb switches
 * between things in progress — browsing everything available is the library's
 * job, and the menu's last row is a door to it.
 *
 * A scenario whose summary has not loaded still appears. Dropping it would
 * make a session the user owns unreachable from the switcher because of a
 * request that has not landed yet.
 */
export function scenariosInProgress(
  sessions: Session[],
  titles: ScenarioTitle[],
): ScenarioInProgress[] {
  const titleById = new Map(titles.map((t) => [t.id, t.title]));
  const counts = new Map<string, { count: number; touched: string }>();

  sessions.forEach((s) => {
    const existing = counts.get(s.scenarioId);
    if (existing) {
      existing.count += 1;
      if (s.lastModifiedAt > existing.touched) existing.touched = s.lastModifiedAt;
    } else {
      counts.set(s.scenarioId, { count: 1, touched: s.lastModifiedAt });
    }
  });

  return [...counts.entries()]
    .sort((a, b) => (a[1].touched < b[1].touched ? 1 : a[1].touched > b[1].touched ? -1 : 0))
    .map(([id, { count }]) => ({
      id,
      title: titleById.get(id) ?? "Untitled scenario",
      sessionCount: count,
    }));
}
